import React from "react";
import { Button, ButtonProps } from "antd";
import styled, { css } from "styled-components";

export interface CustomButtonProps extends Omit<ButtonProps, "type"> {
  LeftIconEnabled?: boolean;
  RightIconEnabled?: boolean;
  LeftIcon?: React.ComponentType<any>;
  RightIcon?: React.ComponentType<any>;
  hasTitle?: boolean;
  customSize?: "XLarge" | "Large";
  customType?: "Primary" | "Secondary" | "Link" | "Text";
  children?: React.ReactNode;
  onClickLeftIcon?: () => void;
  onClickRightIcon?: () => void;
}

const sizeStyles = css<{ customSize?: "XLarge" | "Large" }>`
  ${(props) =>
    props.customSize === "XLarge" &&
    css`
      height: 48px;
      padding: 0 20px;
      font-size: 15px;
    `}
  ${(props) =>
    props.customSize === "Large" &&
    css`
      height: 40px;
      padding: 0 16px;
      font-size: 14px;
    `}
`;

const typeStyles = css<{
  customType?: "Primary" | "Secondary" | "Link" | "Text";
}>`
  /* ------------------- Primary (Dark Gray) ------------------- */
  ${(props) =>
    props.customType === "Primary" &&
    css`
      background-color: var(--color-btn-dark, #23272e);
      color: var(--color-text-on-primary, #ffffff);
      border: 1px solid transparent;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);

      &:hover:not(:disabled) {
        background-color: #323842 !important;
        color: #ffffff !important;
        border-color: transparent !important;
      }

      &:active:not(:disabled) {
        background-color: var(--color-text-primary, #17191c) !important;
        color: #ffffff !important;
        transform: translateY(1px);
      }
    `}

  /* ------------------- Secondary (Bordered) ------------------- */
  ${(props) =>
    props.customType === "Secondary" &&
    css`
      background-color: var(--color-surface, #ffffff);
      color: var(--color-text-primary, #17191c);
      border: 1px solid var(--color-border, #c9cdd3);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

      &:hover:not(:disabled) {
        border-color: var(--color-border-hover, #9298a1) !important;
        background-color: var(--color-surface-muted, #f6f7f9) !important;
        color: var(--color-text-primary, #17191c) !important;
      }

      &:active:not(:disabled) {
        background-color: #edeef2 !important;
        color: var(--color-text-primary, #17191c) !important;
      }
    `}

  /* ------------------- Link ------------------- */
  ${(props) =>
    props.customType === "Link" &&
    css`
      background: transparent;
      border: none;
      color: var(--color-text-primary, #17191c);
      box-shadow: none;
      padding: 0 4px;
      text-decoration: none;

      &:hover:not(:disabled) {
        background-color: transparent !important;
        color: var(--color-text-secondary, #60656d) !important;
        text-decoration: none !important;
      }

      &:active:not(:disabled) {
        background-color: transparent !important;
        color: var(--color-btn-dark, #23272e) !important;
        text-decoration: none !important;
      }
    `}
  /* ------------------- Text ------------------- */
  ${(props) =>
    props.customType === "Text" &&
    css`
      background: transparent;
      border: none;
      color: var(--color-text-secondary, #60656d);
      box-shadow: none;

      &:hover:not(:disabled) {
        background-color: var(--color-surface-muted, #f6f7f9) !important;
        color: var(--color-text-primary, #17191c) !important;
      }

      &:active:not(:disabled) {
        background-color: #edeef2 !important;
        color: var(--color-text-primary, #17191c) !important;
      }
    `}

  /* ------------------- Common Disabled State ------------------- */
  &:disabled,
  &.ant-btn-disabled {
    background-color: var(--color-disabled-bg, #e5e7ea) !important;
    color: var(--color-disabled-text, #8b9098) !important;
    border-color: transparent !important;
    box-shadow: none !important;
    cursor: not-allowed;
    text-decoration: none !important;
  }
`;

const StyledButton = styled(Button)<{
  customSize?: "XLarge" | "Large";
  customType?: "Primary" | "Secondary" | "Link" | "Text";
}>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: var(--radius-button, 8px);
  transition: all var(--transition-ui, 160ms ease);
  font-weight: 500;

  .left-icon,
  .right-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  ${sizeStyles}
  ${typeStyles}
`;

const CustomButton: React.FC<CustomButtonProps> = ({
  LeftIconEnabled,
  RightIconEnabled,
  LeftIcon,
  RightIcon,
  hasTitle = true,
  customSize = "Large",
  customType = "Primary",
  children,
  onClickLeftIcon,
  onClickRightIcon,
  ...props
}) => {
  const handleIconClick = (event: React.MouseEvent, callback?: () => void) => {
    event.stopPropagation();
    if (callback) callback();
  };

  return (
    <StyledButton
      className="iran"
      {...props}
      customSize={customSize}
      customType={customType}
    >
      {RightIconEnabled && RightIcon && (
        <RightIcon
          className="icon right-icon"
          onClick={(e: React.MouseEvent) => handleIconClick(e, onClickRightIcon)}
        />
      )}

      {hasTitle && children}

      {LeftIconEnabled && LeftIcon && (
        <LeftIcon
          className="icon left-icon"
          onClick={(e: React.MouseEvent) => handleIconClick(e, onClickLeftIcon)}
        />
      )}
    </StyledButton>
  );
};

export default CustomButton;
